
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Shield, ShieldCheck, ShieldX } from 'lucide-react';

interface BackgroundCheckProps {
  userId?: string;
  userType: 'technician' | 'customer';
  userName?: string;
}

const BackgroundCheckComponent: React.FC<BackgroundCheckProps> = ({ 
  userId, 
  userType = 'technician',
  userName 
}) => {
  const { toast } = useToast();
  const [status, setStatus] = useState<'idle' | 'pending' | 'completed' | 'failed'>('idle');
  const [checkType, setCheckType] = useState<string>('basic');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [checkHistory, setCheckHistory] = useState<Array<{
    id: string;
    type: string;
    date: Date;
    status: 'pending' | 'completed' | 'failed';
    result?: 'pass' | 'flag' | 'review';
  }>>([
    {
      id: 'check-1',
      type: 'Basic Identity',
      date: new Date(2025, 2, 15),
      status: 'completed',
      result: 'pass'
    }
  ]);

  const handleRequestCheck = () => {
    // In a real implementation, this would call an API to request a background check
    setStatus('pending');
    toast({
      title: "Background check requested",
      description: `A ${checkType} background check has been requested for this ${userType}.`,
    });
    
    // Mock background check process with timeout
    setTimeout(() => {
      const newCheck = {
        id: `check-${checkHistory.length + 1}`,
        type: checkType === 'basic' ? 'Basic Identity' : 
              checkType === 'standard' ? 'Standard Employment' : 
              'Enhanced Security',
        date: new Date(),
        status: 'pending' as const
      };
      
      setCheckHistory([...checkHistory, newCheck]);
    }, 1000);
  };

  const handleVerifyCode = () => {
    // In a real implementation, this would verify the code against an API
    if (verificationCode.length < 5) {
      toast({
        title: "Invalid code",
        description: "Please enter a valid verification code.",
        variant: "destructive"
      });
      return;
    }

    setStatus('completed');
    
    // Update the latest check to completed
    const updatedHistory = [...checkHistory];
    if (updatedHistory.length > 0) {
      const lastIndex = updatedHistory.length - 1;
      updatedHistory[lastIndex] = {
        ...updatedHistory[lastIndex],
        status: 'completed',
        result: 'pass'
      };
    }
    
    setCheckHistory(updatedHistory);
    
    toast({
      title: "Verification successful",
      description: "Background check has been completed successfully.",
    });
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Background Verification</CardTitle>
            <CardDescription>
              Manage and track background checks for {userType === 'technician' ? 'technicians' : 'customers'}
            </CardDescription>
          </div>
          {status === 'completed' && (
            <Badge className="bg-green-100 text-green-800 border-green-300 flex items-center gap-1">
              <ShieldCheck className="h-4 w-4" />
              Verified
            </Badge>
          )}
          {status === 'failed' && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <ShieldX className="h-4 w-4" />
              Failed
            </Badge>
          )}
          {status === 'idle' && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Shield className="h-4 w-4" />
              Not Verified
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {status === 'idle' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="check-type">Check Type</Label>
              <Select value={checkType} onValueChange={setCheckType}>
                <SelectTrigger id="check-type">
                  <SelectValue placeholder="Select check type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic Identity Check</SelectItem>
                  <SelectItem value="standard">Standard Employment Check</SelectItem>
                  <SelectItem value="enhanced">Enhanced Security Check</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {checkType === 'basic' ? 
                  'Verifies identity and basic criminal record.' : 
                  checkType === 'standard' ? 
                    'Includes identity, criminal, employment history verification.' : 
                    'Comprehensive check including identity, criminal history, employment verification, credit history, and reference checks.'}
              </p>
            </div>
            <Button onClick={handleRequestCheck} className="w-full">
              Request Background Check
            </Button>
          </div>
        )}
        
        {status === 'pending' && (
          <div className="space-y-4">
            <p className="text-sm">
              A verification code has been sent to the {userType}'s email address. Please enter it below to complete the process.
            </p>
            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input 
                id="verification-code" 
                value={verificationCode} 
                onChange={e => setVerificationCode(e.target.value)}
                placeholder="Enter verification code" 
                maxLength={6}
              />
            </div>
            <Button onClick={handleVerifyCode} className="w-full">
              Verify Code
            </Button>
          </div>
        )}
        
        {checkHistory.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-2">Verification History</h4>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkHistory.map((check) => (
                    <TableRow key={check.id}>
                      <TableCell className="font-medium">{check.type}</TableCell>
                      <TableCell>{check.date.toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Badge variant={check.status === 'completed' ? 'outline' : 'secondary'}>
                          {check.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {check.result && (
                          <Badge 
                            className={
                              check.result === 'pass' ? 'bg-green-100 text-green-800 border-green-300' : 
                              check.result === 'flag' ? 'bg-red-100 text-red-800 border-red-300' : 
                              'bg-yellow-100 text-yellow-800 border-yellow-300'
                            }
                          >
                            {check.result}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BackgroundCheckComponent;
